"use client";

import { createContext } from "react";
import type { UIAction } from "@huub-web/ui";

/**
 * A page-level action that the agent can trigger.
 * Registered by action-bearing templates (AddCard, ModelCard) on mount.
 */
export type PageAction = {
  /**
   * Unique identifier for this action registration
   */
  id: string;
  /**
   * Human-readable label shown to the agent (e.g., "Toevoegen: Loondienst")
   */
  label: string;
  /**
   * The underlying UI action to execute
   */
  action: UIAction;
  /**
   * Whether this action is destructive (requires agent confirmation)
   */
  destructive?: boolean;
};

export type PageContentItem = {
  type: "card" | "cta" | "data-item" | "search";
  label: string;
  description?: string;
  meta?: Record<string, string | number | boolean>;
  action?: UIAction;
};

export type PageContentContextState = {
  /**
   * Register a page action. Called by templates on mount.
   */
  registerAction: (id: string, action: PageAction) => void;
  /**
   * Deregister a page action. Called by templates on unmount.
   */
  deregisterAction: (id: string) => void;
  /**
   * Register a content item. Called by templates on mount.
   */
  registerContent: (id: string, item: PageContentItem) => void;
  /**
   * Deregister a content item. Called by templates on unmount.
   */
  deregisterContent: (id: string) => void;
  /**
   * Record that the user has explicitly confirmed a destructive page action
   * out-of-band (e.g. clicked through the delete-confirmation modal). The
   * name must match the registered page action's `label`. Forwarded to the
   * unified ui-context WS message as `confirmedActions` so the backend's
   * trigger_page_action tool can authorize it (SMA-1539).
   */
  confirmAction: (actionName: string) => void;
};

const noop = () => {};

export const PageContentContext = createContext<PageContentContextState>({
  registerAction: noop,
  deregisterAction: noop,
  registerContent: noop,
  deregisterContent: noop,
  confirmAction: noop,
});

/**
 * Tracks whether a PageContentProvider exists above in the tree.
 * Used by TemplatesParser to ensure only ONE provider wraps the outermost level.
 */
export const PageContentProviderActive = createContext(false);
